import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { CircularProgress, Typography, TextField, Button, Box, Tab, Tabs, Card, CardContent, MenuItem, FormHelperText, InputLabel, Select, FormControl } from '@mui/material';
import BankaBilgileriAPI, { BankaBilgileriGuncelleRequest } from '../../../apis/BankaBilgileriAPI';
import { InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const validationSchema = Yup.object({
  bankaAdi: Yup.string()
    .required('Banka adı zorunlu'),
});

const BankaBilgileri: React.FC = () => {
  const [bankalar, setBankalar] = useState<BankaBilgileriGuncelleRequest[]>([]);
  const [isLoad, setIsLoad] = useState(true);
  const [message, setMessage] = useState<string>('');
  const [aktifTabIndex, setAktifTabIndex] = useState(0);
  const [temp, setTemp] = useState();
  const [showPassword, setShowPassword] = useState(false);

  console.log("banka çalıştı");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoad(true);
      try {
        const response = await BankaBilgileriAPI.getBankaBilgileri();
        if (Array.isArray(response?.data)) {
          const data = response.data as BankaBilgileriGuncelleRequest[];
          if (data.length > 0) {
            setBankalar(data);
            setAktifTabIndex(0);       // İlk bankayı seç
          } else {
            setMessage('Bu ID’ye sahip banka bulunamadı.');
          }
        } else {
          setMessage('Beklenen formatta veri gelmedi.');
        }
      } catch (err) {
        console.error(err);
        setMessage('Bankalar alınırken hata oluştu.');
      } finally {
        setIsLoad(false);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setAktifTabIndex(newIndex);
    setMessage('');
  };

  const handleSubmit = async (values: BankaBilgileriGuncelleRequest) => {
    try {
      await BankaBilgileriAPI.updateBankaBilgileri(values);
      setMessage('Güncelleme başarılı.');
    } catch (err) {
      console.error(err);
      setMessage('Güncelleme sırasında hata oluştu.');
    }
  };

  if (isLoad) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
        <CircularProgress />
      </div>
    );
  }

  // Eğer hiç banka yoksa hata mesajı
  if (bankalar.length === 0) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        {message || 'Banka verisi yüklenemedi.'}
      </Typography>
    );
  }

  // Şu anda seçili banka objesi
  const seciliBanka = bankalar[aktifTabIndex];

  return (
    <div className="container py-4">
      <Typography variant="h5" gutterBottom>
        Banka Güncelle
      </Typography>

      {/* Dinamik Tab Başlıkları */}
      <Tabs
        value={aktifTabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {bankalar.map((b, idx) => (
          <Tab
            key={b.id ?? idx}
            label={`${b.bankaAdi} (${b.bankaKodu})`}
          />
        ))}
      </Tabs>

      <Card sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2, boxShadow: 4 }}>
        <CardContent>
          <Formik<BankaBilgileriGuncelleRequest>
            enableReinitialize
            initialValues={seciliBanka}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, handleBlur, errors, touched, dirty, isValid }) => (
              <Form>
                <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))" gap={2}>
                  {[
                    { name: 'bankaAdi', label: 'Banka Adı' },
                    { name: 'bankaKodu', label: 'Banka Kodu' },
                    { name: 'uyeNo', label: 'Üye No' },
                    { name: 'posNo', label: 'POS No' },
                    { name: 'sifre', label: 'Şifre', type: 'password' },
                    { name: 'posnetUrl', label: 'Posnet URL' },
                    { name: 'posnetXmlUrl', label: 'Posnet XML URL' },
                    { name: 'basariliUrl', label: 'Başarılı URL' },
                    { name: 'basarisizUrl', label: 'Başarısız URL' },
                    { name: 'islem3D', label: 'İşlem 3D', type: 'select' },
                    { name: 'vezneKodu', label: 'Vezne Kodu', type: 'number' },
                    { name: 'aktifEh', label: 'Aktif (E/H) (Bu parametreyi H olarak ayarlarsanız tahsilat web uygulamasında ödeme işlemi duracaktır)', type: 'select' },
                    { name: 'taksitSayisi', label: 'Taksit Sayısı', type: 'number' },
                    { name: 'vadeFarkliMi', label: 'Vade Farklı mı', type: 'select' },
                    { name: 'anlasmaliMi', label: 'Anlaşmalı mı', type: 'select' },
                    { name: 'kurumAdi', label: 'Kurum Adı' },
                    { name: 'kullaniciKodu', label: 'Kullanıcı Kodu' },
                    { name: 'guvenlikKodu', label: 'Güvenlik Kodu' },
                    { name: 'odemeYontemi3D', label: 'Ödeme Yöntemi 3D' },
                  ].map(field => {

                    if (field.name === 'aktifEh') {
                      return (
                        <FormControl
                          key={field.name}
                          fullWidth
                          size="small"
                          error={Boolean(touched.aktifEh && errors.aktifEh)}
                        >
                          <InputLabel id="aktifEh-label">
                            Aktif (E/H) &nbsp;
                            <Tooltip
                              title="Bu parametreyi H olarak ayarlarsanız tahsilat web uygulamasında ödeme işlemi duracaktır."
                              placement="right"
                              arrow
                            >
                              <InfoOutlinedIcon
                                fontSize="small"
                                sx={{ verticalAlign: 'middle', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </InputLabel>
                          <Select
                            labelId="aktifEh-label"
                            id="aktifEh"
                            name="aktifEh"
                            value={(values as any).aktifEh ?? ''}
                            label="Aktif (E/H)"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value=""><em>Seçiniz</em></MenuItem>
                            <MenuItem value="E">E</MenuItem>
                            <MenuItem value="H">H</MenuItem>
                          </Select>
                          <FormHelperText>
                            {touched.aktifEh && errors.aktifEh
                              ? errors.aktifEh
                              : ''}
                          </FormHelperText>
                        </FormControl>
                      )
                    }

                    // Select alanları için
                    if (field.name === 'islem3D' || field.name === 'vadeFarkliMi' || field.name === 'anlasmaliMi') {
                      return (
                        <TextField
                          key={field.name}
                          select
                          fullWidth
                          label={field.label}
                          name={field.name}
                          value={(values as any)[field.name] ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          size="small"
                          error={Boolean(
                            touched[field.name as keyof typeof touched] &&
                            errors[field.name as keyof typeof errors]
                          )}
                          helperText={
                            touched[field.name as keyof typeof touched] &&
                            (errors[field.name as keyof typeof errors] as string)
                          }
                        >
                          <MenuItem value="">
                            {/* null değeri için */}
                            Seçiniz
                          </MenuItem>
                          <MenuItem value="E">E</MenuItem>
                          <MenuItem value="H">H</MenuItem>
                        </TextField>
                      );
                    }
                    // Sadece "sifre" alanı için göster/gizle butonu 
                    if (field.name === 'sifre') {
                      return (
                        <TextField
                          key={field.name}
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          label={field.label}
                          name={field.name}
                          value={(values as any)[field.name] ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          size="small"
                          error={Boolean(
                            touched[field.name as keyof typeof touched] &&
                            errors[field.name as keyof typeof errors]
                          )}
                          helperText={
                            touched[field.name as keyof typeof touched] &&
                            (errors[field.name as keyof typeof errors] as string)
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={() => setShowPassword(prev => !prev)}
                                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      );
                    }

                    // Diğer tüm alanlar için varsayılan TextField
                    return (
                      <TextField
                        key={field.name}
                        fullWidth
                        type={(field as any).type || 'text'}
                        label={field.label}
                        name={field.name}
                        value={(values as any)[field.name] ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                        error={Boolean(
                          touched[field.name as keyof typeof touched] &&
                          errors[field.name as keyof typeof errors]
                        )}
                        helperText={
                          touched[field.name as keyof typeof touched] &&
                          (errors[field.name as keyof typeof errors] as string)
                        }
                      />
                    );
                  })}
                </Box>

                <Box textAlign="right" mt={3}>
                  <Button type="submit" variant="contained" disabled={!dirty || !isValid}>
                    Güncelle
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>

      {message && (
        <Typography color="primary" align="center" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </div>
  );
};

export default BankaBilgileri;